import type { CreateDocumentParams, UpdateDocumentParams } from '@/api/firebase';
import { FirebaseApi } from '@/api/firebase';
import type { FirestoreQuery, QueryResult } from '@shared/types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { firebaseKeys } from './firebase.keys';

export function useCreateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      connectionId: string;
      collectionPath: string;
      body: CreateDocumentParams;
    }) => FirebaseApi.createDocument(params.connectionId, params.collectionPath, params.body),
    onSuccess: (_, { connectionId, collectionPath }) => {
      queryClient.invalidateQueries({
        queryKey: firebaseKeys.collections(connectionId),
      });
      queryClient.invalidateQueries({
        queryKey: firebaseKeys.documentsInfinitePrefix(connectionId, collectionPath),
      });
    },
  });
}

export function useUpdateDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      connectionId: string;
      collectionPath: string;
      documentId: string;
      body: UpdateDocumentParams;
    }) =>
      FirebaseApi.updateDocument(
        params.connectionId,
        params.collectionPath,
        params.documentId,
        params.body
      ),
    onSuccess: (_, { connectionId, collectionPath, documentId }) => {
      queryClient.invalidateQueries({
        queryKey: firebaseKeys.document(connectionId, collectionPath, documentId),
      });
      queryClient.invalidateQueries({
        queryKey: firebaseKeys.documentsInfinitePrefix(connectionId, collectionPath),
      });
    },
  });
}

export function useDeleteDocument() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (params: { connectionId: string; collectionPath: string; documentId: string }) =>
      FirebaseApi.deleteDocument(params.connectionId, params.collectionPath, params.documentId),
    onSuccess: (_, { connectionId, collectionPath, documentId }) => {
      queryClient.invalidateQueries({
        queryKey: firebaseKeys.document(connectionId, collectionPath, documentId),
      });
      queryClient.invalidateQueries({
        queryKey: firebaseKeys.documentsInfinitePrefix(connectionId, collectionPath),
      });
    },
  });
}

function getBackendErrorMessage(error: unknown): string {
  const ax = error as { response?: { data?: { error?: { message?: string } } }; message?: string };
  const fromBody = ax?.response?.data?.error?.message;
  if (typeof fromBody === 'string' && fromBody.trim()) return fromBody.trim();
  if (error instanceof Error && error.message) return error.message;
  return 'Unknown error';
}

function processQueryResponse(
  response: Awaited<ReturnType<typeof FirebaseApi.queryDocuments>>
): QueryResult {
  if (!response.validation.valid && response.validation.errors.length > 0) {
    const errorMessages = response.validation.errors
      .map((e) => `${e.path}: ${e.message}`)
      .join('\n');
    throw new Error(`Query validation failed:\n${errorMessages}`);
  }

  if (response.indexError) {
    const msg =
      typeof response.indexError.indexUrl === 'string'
        ? response.indexError.indexUrl
        : 'Query requires a composite index.';
    throw new Error(msg);
  }

  if (!response.results) {
    return {
      documents: [],
      meta: {
        executionMs: 0,
        readCount: 0,
        source: 'mock',
      },
    };
  }

  return response.results;
}

export function useRunFirestoreQuery() {
  return useMutation({
    mutationFn: async (params: {
      connectionId: string;
      collectionName: string;
      query: FirestoreQuery;
    }): Promise<QueryResult> => {
      try {
        const response = await FirebaseApi.queryDocuments(
          params.connectionId,
          params.collectionName,
          {
            connectionId: params.connectionId,
            ...params.query,
          }
        );
        return processQueryResponse(response);
      } catch (error) {
        throw new Error(getBackendErrorMessage(error));
      }
    },
  });
}

export function useValidateFirestoreQueryPreview() {
  return useMutation({
    mutationFn: async (params: {
      connectionId: string;
      collectionName: string;
      query: FirestoreQuery;
    }): Promise<QueryResult> => {
      try {
        const response = await FirebaseApi.validateQueryPreview(
          params.connectionId,
          params.collectionName,
          {
            connectionId: params.connectionId,
            ...params.query,
          }
        );
        return processQueryResponse(response);
      } catch (error) {
        throw new Error(getBackendErrorMessage(error));
      }
    },
  });
}
